import { formatDistanceToNow } from "date-fns";
import { MessageCircle } from "lucide-react";

import type { SearchHistoryItem } from "./types";

const HistoryItem = ({
  item,
  onClick,
  hasSearchQuery,
  searchQuery,
}: HistoryItemProps) => {
  const highlightSearchTerms = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return text;

    // Split search term into individual words and filter out empty strings
    const searchWords = searchTerm
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 0);

    if (searchWords.length === 0) return text;

    // Create a regex that matches any of the search words (case insensitive)
    const regex = new RegExp(
      `(${searchWords
        .map(
          (word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // Escape special regex characters
        )
        .join("|")})`,
      "gi"
    );

    const parts = text.split(regex);

    return parts.map((part, index) => {
      const isHighlight = searchWords.some(
        (word) => part.toLowerCase() === word.toLowerCase()
      );

      if (isHighlight) {
        return (
          <span
            key={index}
            className="bg-gray-200/80 border border-gray-300/60 dark:bg-neutral-700/60 dark:border-neutral-600/40 px-1 py-0.5 rounded text-xs font-medium"
            style={{ color: "#497b8b" }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div
      className="p-3 rounded-lg cursor-pointer transition-all duration-200 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:shadow-sm dark:bg-neutral-800/50 dark:hover:bg-neutral-700/70 dark:border-neutral-700/50"
      onClick={() => onClick(item)}
    >
      <div className="flex items-start gap-3">
        <MessageCircle
          size={16}
          className="mt-0.5 flex-shrink-0 text-primary"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm leading-relaxed line-clamp-1 text-gray-800 dark:text-gray-200">
            {item.query}
          </p>
          {/* Show matched sentence if available and search query exists */}
          {hasSearchQuery && item.matchedSentence && (
            <p className="text-xs mt-1 italic line-clamp-1 text-gray-600 dark:text-gray-400">
              "{highlightSearchTerms(item.matchedSentence, searchQuery || "")}"
            </p>
          )}
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatDistanceToNow(item.date, { addSuffix: true })}
          </span>
        </div>
      </div>
    </div>
  );
};

type HistoryItemProps = {
  item: SearchHistoryItem;
  onClick: (item: SearchHistoryItem) => void;
  hasSearchQuery?: boolean;
  searchQuery?: string;
};

export default HistoryItem;
